import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Divider,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeAccionFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { plantillas } from './PlantillasFactura';
import tiposDocumentosJson from 'utils/tiposDocumentos.json';

const tiposDocumento =
  Array.isArray(tiposDocumentosJson) && tiposDocumentosJson[3]?.tiposdocumentocliente
    ? tiposDocumentosJson[3]?.tiposdocumentocliente
    : [];

export const PROGRAMA_CONFIGURACION_FACTURA = 'configuracionfacturas';

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const configuracionPorDefecto = () => ({
  plantillaPorDefecto: '80mm-basica',
  leyendaPiePagina: '',
  mensajeGracias: 'Gracias por su compra',
  mensajeGarantia: '',
  mensajeDevolucion: '',
  mostrarInfoEmpresa: true,
  nombreEmpresa: '',
  direccionEmpresa: '',
  telefonoEmpresa: '',
  emailEmpresa: '',
  rncEmpresa: '',
  sitioWebEmpresa: '',
  mostrarNCF: true,
  mostrarObservaciones: true,
  mostrarDescuentos: true,
  mostrarImpuestos: true,
  mostrarSubtotalAntesDescuentos: true,
  cortarPapelAutomatico: false,
  imprimirLogo: false,
  rutaLogo: '',
  mostrarCodigoBarras: false,
  mostrarQR: false,
  textoQR: '',
});

/**
 * ConfiguracionFactura: permite configurar plantilla, leyendas, info de empresa y opciones de visualización
 * por tipo de documento fiscal. Persiste en localStorage y en el backend.
 */
const ConfiguracionFactura = ({ open = true, onClose = () => {} }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_CONFIGURACION_FACTURA),
    [roleDetalles]
  );
  const puedeModificar = useMemo(
    () => puedeAccionFacturacion(roleDetalles, PROGRAMA_CONFIGURACION_FACTURA, 'modificar'),
    [roleDetalles]
  );

  const [tabActiva, setTabActiva] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState(tiposDocumento[0] || 'Factura');
  const [configuracion, setConfiguracion] = useState(configuracionPorDefecto());

  const cargarConfiguracion = useCallback(
    (tipoDoc) => {
      const base = configuracionPorDefecto();
      try {
        let porTipo = {};
        const guardado = localStorage.getItem('configuracionFacturaPorTipo');
        if (guardado) {
          porTipo = JSON.parse(guardado);
        } else {
          const legacy = localStorage.getItem('configuracionFactura');
          if (legacy) {
            try {
              porTipo = { Factura: JSON.parse(legacy) };
              localStorage.setItem('configuracionFacturaPorTipo', JSON.stringify(porTipo));
            } catch {
              /* ignore */
            }
          }
        }
        const config = porTipo[tipoDoc];
        if (config) setConfiguracion({ ...base, ...config });
        else setConfiguracion(base);

        if (selectedCompany?.db_empresa && usuarioLog) {
          fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              tipo_documento: tipoDoc,
            }),
          })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data?.configuracion) {
                const { tipoDocumento: _td, ...rest } = data.configuracion;
                setConfiguracion((prev) => ({ ...prev, ...rest }));
              }
            })
            .catch(() => {});
        }
      } catch (error) {
        console.error('Error al cargar configuración:', error);
        setConfiguracion(base);
      }
    },
    [selectedCompany, usuarioLog, claveLog, servidor]
  );

  useEffect(() => {
    if (open && puedeLeer) cargarConfiguracion(tipoDocumento);
  }, [open, puedeLeer, tipoDocumento, cargarConfiguracion]);

  const guardarConfiguracion = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para guardar la configuración.');
      return;
    }
    setGuardando(true);
    try {
      const configConTipo = { ...configuracion, tipoDocumento };
      const guardado = localStorage.getItem('configuracionFacturaPorTipo');
      const porTipo = guardado ? JSON.parse(guardado) : {};
      porTipo[tipoDocumento] = configuracion;
      localStorage.setItem('configuracionFacturaPorTipo', JSON.stringify(porTipo));

      if (selectedCompany?.db_empresa) {
        try {
          const response = await fetch(servidor + '/api/facturabackend_guardarconfiguracion', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              configuracion: configConTipo,
              tipo_documento: tipoDocumento,
              usuario: usuarioLog || '',
              password: claveLog || '',
              ...(selectedCompany && { id_empresa: selectedCompany }),
            }),
          });
          if (!response.ok) {
            console.warn('No se pudo guardar en el servidor, pero se guardó localmente');
          }
        } catch (e) {
          console.warn('Error al guardar en servidor:', e);
        }
      }

      showSuccess('Configuración guardada exitosamente');
      if (onClose && typeof onClose === 'function') {
        setTimeout(() => onClose(), 800);
      }
    } catch {
      showError('Error al guardar la configuración');
    } finally {
      setGuardando(false);
    }
  };

  const actualizarConfiguracion = (campo, valor) => {
    setConfiguracion((prev) => ({ ...prev, [campo]: valor }));
  };

  const resetearConfiguracion = () => {
    if (!puedeModificar) {
      showError('No tiene permisos para resetear la configuración.');
      return;
    }
    if (
      window.confirm(
        '¿Está seguro de resetear la configuración de este tipo de documento a los valores por defecto?'
      )
    ) {
      setConfiguracion(configuracionPorDefecto());
      showSuccess('Configuración reseteada');
    }
  };

  const handleChangeTipoDocumento = (value) => {
    setTipoDocumento(value);
    cargarConfiguracion(value);
  };

  const tabSx = {
    minHeight: 48,
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.88rem',
      minHeight: 48,
      color: FP.color.textSecondary,
      gap: 0.6,
    },
    '& .Mui-selected': { color: FP.color.navy },
    '& .MuiTabs-indicator': {
      backgroundColor: FP.color.navy,
      height: 3,
      borderRadius: '3px 3px 0 0',
    },
  };

  const renderContenidoTabs = () => (
    <>
      <Box sx={{ mb: 2.5 }}>
        <FormControl fullWidth size="small" sx={{ maxWidth: 420, ...fpInputSx }}>
          <InputLabel shrink>Tipo de documento</InputLabel>
          <Select
            notched
            value={tipoDocumento}
            onChange={(e) => handleChangeTipoDocumento(e.target.value)}
            label="Tipo de documento"
            disabled={!puedeModificar}
          >
            {tiposDocumento.map((tipo) => (
              <MenuItem key={tipo} value={tipo}>
                {tipo}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography sx={{ display: 'block', mt: 0.5, color: FP.color.textSecondary, fontSize: '0.78rem' }}>
          La configuración se guarda por tipo de documento. Seleccione el documento al que desea aplicar o
          editar la configuración.
        </Typography>
      </Box>

      <Box sx={{ borderBottom: `1px solid ${FP.color.border}`, mb: 2 }}>
        <Tabs
          value={tabActiva}
          onChange={(_, v) => setTabActiva(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={tabSx}
        >
          <Tab icon={<PrintIcon />} iconPosition="start" label="Plantilla" />
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="Leyendas" />
          <Tab icon={<BusinessIcon />} iconPosition="start" label="Empresa" />
          <Tab icon={<VisibilityIcon />} iconPosition="start" label="Visualización" />
        </Tabs>
      </Box>

      {tabActiva === 0 && (
        <Box>
          <Typography sx={{ mb: 1.5, fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
            Plantilla por defecto
          </Typography>
          <FormControl fullWidth sx={{ mb: 2.5, ...fpInputSx }}>
            <InputLabel shrink>Plantilla por defecto</InputLabel>
            <Select
              notched
              value={configuracion.plantillaPorDefecto}
              onChange={(e) => actualizarConfiguracion('plantillaPorDefecto', e.target.value)}
              label="Plantilla por defecto"
              disabled={!puedeModificar}
            >
              {Object.entries(plantillas).map(([id, plantilla]) => (
                <MenuItem key={id} value={id}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: FP.color.textPrimary }}>
                      {plantilla.nombre}
                    </Typography>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                      {plantilla.descripcion}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Grid container spacing={2}>
            {Object.entries(plantillas).map(([id, plantilla]) => {
              const seleccionada = configuracion.plantillaPorDefecto === id;
              return (
                <Grid item xs={12} sm={6} md={4} key={id}>
                  <Card
                    onClick={() => puedeModificar && actualizarConfiguracion('plantillaPorDefecto', id)}
                    sx={{
                      cursor: puedeModificar ? 'pointer' : 'not-allowed',
                      border: `1px solid ${seleccionada ? FP.color.accentBlue : FP.color.border}`,
                      bgcolor: seleccionada ? FP.color.accentBlueSoft : FP.color.cardBg,
                      borderRadius: 2,
                      boxShadow: seleccionada ? FP.shadow.soft : 'none',
                      transition: 'border-color .2s, background-color .2s',
                      '&:hover': puedeModificar
                        ? { borderColor: FP.color.accentBlue, bgcolor: FP.color.accentBlueSoft }
                        : {},
                    }}
                  >
                    <CardContent>
                      <Typography sx={{ fontWeight: 700, mb: 0.5, color: FP.color.textPrimary }}>
                        {plantilla.nombre}
                      </Typography>
                      <Typography sx={{ color: FP.color.textSecondary, mb: 1, fontSize: '0.82rem' }}>
                        {plantilla.descripcion}
                      </Typography>
                      <Chip
                        label={plantilla.tipo === '80mm' ? 'Ticket 80mm' : 'Carta 8.5x11'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: plantilla.tipo === '80mm' ? FP.color.accentBlueSoft : FP.color.surfaceAlt,
                          color: plantilla.tipo === '80mm' ? FP.color.accentBlue : FP.color.textSecondary,
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Divider sx={{ my: 3, borderColor: FP.color.border }} />

          <Typography sx={{ mb: 1.5, fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
            Configuración de impresión
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configuracion.cortarPapelAutomatico}
                    onChange={(e) => actualizarConfiguracion('cortarPapelAutomatico', e.target.checked)}
                    disabled={!puedeModificar}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: FP.color.textPrimary }}>
                      Cortar papel automático
                    </Typography>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                      Para impresoras térmicas con cortador
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configuracion.imprimirLogo}
                    onChange={(e) => actualizarConfiguracion('imprimirLogo', e.target.checked)}
                    disabled={!puedeModificar}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: FP.color.textPrimary }}>
                      Imprimir logo
                    </Typography>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                      Mostrar logo de la empresa en la factura
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            {configuracion.imprimirLogo && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ruta del logo"
                  value={configuracion.rutaLogo}
                  onChange={(e) => actualizarConfiguracion('rutaLogo', e.target.value)}
                  placeholder="/ruta/al/logo.png"
                  helperText="Ruta relativa o URL del logo"
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {tabActiva === 1 && (
        <Box>
          <Typography sx={{ mb: 1.5, fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
            Mensajes y leyendas personalizadas
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Leyenda de pie de página"
                value={configuracion.leyendaPiePagina}
                onChange={(e) => actualizarConfiguracion('leyendaPiePagina', e.target.value)}
                multiline
                rows={3}
                placeholder="Ej: Esta factura es un comprobante de pago..."
                helperText="Texto que aparecerá al final de todas las facturas"
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mensaje de agradecimiento"
                value={configuracion.mensajeGracias}
                onChange={(e) => actualizarConfiguracion('mensajeGracias', e.target.value)}
                placeholder="Gracias por su compra"
                helperText="Mensaje que aparece al final de la factura"
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mensaje de garantía"
                value={configuracion.mensajeGarantia}
                onChange={(e) => actualizarConfiguracion('mensajeGarantia', e.target.value)}
                placeholder="Ej: Productos con garantía de 30 días"
                helperText="Información sobre garantía (opcional)"
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Política de devolución"
                value={configuracion.mensajeDevolucion}
                onChange={(e) => actualizarConfiguracion('mensajeDevolucion', e.target.value)}
                multiline
                rows={2}
                placeholder="Ej: Devoluciones dentro de 7 días con recibo..."
                helperText="Política de devolución y cambios (opcional)"
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: FP.color.border }} />

          <Typography sx={{ mb: 1.5, fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
            Códigos y QR
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configuracion.mostrarCodigoBarras}
                    onChange={(e) => actualizarConfiguracion('mostrarCodigoBarras', e.target.checked)}
                    disabled={!puedeModificar}
                  />
                }
                label="Mostrar código de barras"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configuracion.mostrarQR}
                    onChange={(e) => actualizarConfiguracion('mostrarQR', e.target.checked)}
                    disabled={!puedeModificar}
                  />
                }
                label="Mostrar código QR"
              />
            </Grid>
            {configuracion.mostrarQR && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Texto/URL para QR"
                  value={configuracion.textoQR}
                  onChange={(e) => actualizarConfiguracion('textoQR', e.target.value)}
                  placeholder="https://tu-empresa.com/factura/123 o texto personalizado"
                  helperText="URL o texto que se codificará en el QR"
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {tabActiva === 2 && (
        <Box>
          <Typography sx={{ mb: 0.5, fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
            Información de empresa
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, mb: 2, fontSize: '0.82rem' }}>
            Esta información se mostrará en las facturas si está habilitada
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={configuracion.mostrarInfoEmpresa}
                onChange={(e) => actualizarConfiguracion('mostrarInfoEmpresa', e.target.checked)}
                disabled={!puedeModificar}
              />
            }
            label="Mostrar información de empresa en facturas"
            sx={{ mb: 2 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre de la empresa"
                value={configuracion.nombreEmpresa}
                onChange={(e) => actualizarConfiguracion('nombreEmpresa', e.target.value)}
                placeholder="Mi Empresa S.A."
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RNC"
                value={configuracion.rncEmpresa}
                onChange={(e) => actualizarConfiguracion('rncEmpresa', e.target.value)}
                placeholder="123-45678-9"
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección"
                value={configuracion.direccionEmpresa}
                onChange={(e) => actualizarConfiguracion('direccionEmpresa', e.target.value)}
                placeholder="Calle Principal #123, Ciudad"
                multiline
                rows={2}
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={configuracion.telefonoEmpresa}
                onChange={(e) => actualizarConfiguracion('telefonoEmpresa', e.target.value)}
                placeholder="(809) 123-4567"
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={configuracion.emailEmpresa}
                onChange={(e) => actualizarConfiguracion('emailEmpresa', e.target.value)}
                placeholder="contacto@empresa.com"
                type="email"
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sitio web"
                value={configuracion.sitioWebEmpresa}
                onChange={(e) => actualizarConfiguracion('sitioWebEmpresa', e.target.value)}
                placeholder="https://www.empresa.com"
                disabled={!puedeModificar}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {tabActiva === 3 && (
        <Box>
          <Typography sx={{ mb: 0.5, fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
            Opciones de visualización
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, mb: 2, fontSize: '0.82rem' }}>
            Controla qué campos se muestran en las facturas
          </Typography>
          <Grid container spacing={2}>
            {[
              { campo: 'mostrarNCF', label: 'Mostrar NCF (Número de Comprobante Fiscal)' },
              { campo: 'mostrarObservaciones', label: 'Mostrar observaciones' },
              { campo: 'mostrarDescuentos', label: 'Mostrar descuentos' },
              { campo: 'mostrarImpuestos', label: 'Mostrar impuestos' },
              { campo: 'mostrarSubtotalAntesDescuentos', label: 'Mostrar subtotal antes de descuentos' },
            ].map(({ campo, label }) => (
              <Grid item xs={12} sm={6} key={campo}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!configuracion[campo]}
                      onChange={(e) => actualizarConfiguracion(campo, e.target.checked)}
                      disabled={!puedeModificar}
                    />
                  }
                  label={label}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  );

  const renderAcciones = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 3,
        pt: 2,
        borderTop: `1px solid ${FP.color.border}`,
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Button
        onClick={resetearConfiguracion}
        variant="outlined"
        startIcon={<RestartAltIcon />}
        disabled={!puedeModificar}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 1.4,
          borderColor: FP.color.dangerSoft,
          color: FP.color.danger,
          '&:hover': { borderColor: FP.color.danger, bgcolor: FP.color.dangerSoft },
        }}
      >
        Resetear
      </Button>
      <Box sx={{ flex: 1 }} />
      <Button
        onClick={guardarConfiguracion}
        variant="contained"
        startIcon={<SaveIcon />}
        disabled={guardando || !puedeModificar || !usuarioLog}
        sx={{
          textTransform: 'none',
          borderRadius: 1.4,
          fontWeight: 700,
          bgcolor: FP.color.navy,
          color: '#FFFFFF',
          boxShadow: 'none',
          '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
          '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
        }}
      >
        {guardando ? 'Guardando…' : 'Guardar configuración'}
      </Button>
    </Box>
  );

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 6 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <SettingsIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para acceder a la configuración de facturas.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const esPaginaCompleta =
    !onClose || typeof onClose !== 'function' || onClose.toString().includes('() => {}');

  if (esPaginaCompleta) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader
          title="Configuración de facturas"
          subtitle="Plantillas, mensajes, datos de empresa y opciones de visualización"
          kicker="Facturación"
        />
        <Card sx={{ ...cardShellSx, mt: 2 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {renderContenidoTabs()}
            {renderAcciones()}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          maxHeight: '92vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SettingsIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            Configuración de facturas
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: FP.color.textSecondary }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>{renderContenidoTabs()}</DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          gap: 1,
        }}
      >
        <Button
          onClick={resetearConfiguracion}
          variant="outlined"
          startIcon={<RestartAltIcon />}
          disabled={!puedeModificar}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 1.4,
            borderColor: FP.color.dangerSoft,
            color: FP.color.danger,
            '&:hover': { borderColor: FP.color.danger, bgcolor: FP.color.dangerSoft },
          }}
        >
          Resetear
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: FP.color.textSecondary,
            borderRadius: 1.4,
            '&:hover': { bgcolor: FP.color.surfaceAlt },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={guardarConfiguracion}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={guardando || !puedeModificar || !usuarioLog}
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            bgcolor: FP.color.navy,
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
          }}
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfiguracionFactura;
